import { Badge, Button, Card, Group, Loader, SegmentedControl, Stack, Tabs, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FAILURE_STAGE_LABELS,
  getAdminFailures,
  getAdminNotifications,
  resolveFailure,
  type AdminFailure,
  type AdminNotification,
} from '../lib/api.js';

function relTime(ms: number | null): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.round(h / 24);
  return `vor ${d} d`;
}

const CHANNEL_LABEL: Record<string, string> = {
  webpush: 'Web-Push',
  email: 'E-Mail',
  ntfy: 'ntfy',
};

export function AdminOps(): JSX.Element {
  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Text component="h1" fw={800} style={{ fontSize: '1.6rem' }}>
          Betrieb & Diagnose
        </Text>
        <Text c="dimmed" size="sm">
          Ausgehende Benachrichtigungen und zentrale Pipeline-Fehler — alles an einem Ort.
        </Text>
      </Stack>

      <Tabs defaultValue="notifications" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="notifications">🔔 Benachrichtigungen</Tabs.Tab>
          <Tabs.Tab value="failures">⚠️ Fehler</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="notifications" pt="md">
          <NotificationsSection />
        </Tabs.Panel>
        <Tabs.Panel value="failures" pt="md">
          <FailuresSection />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function NotificationsSection(): JSX.Element {
  const [onlyErrors, setOnlyErrors] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-notifications', onlyErrors],
    queryFn: () => getAdminNotifications(onlyErrors, 150),
    refetchInterval: 30_000,
  });

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="lg">
          <Metric label="Zugestellt (7 T.)" value={data?.summary.delivered7d ?? '—'} color="teal.7" />
          <Metric
            label="Fehlgeschlagen (7 T.)"
            value={data?.summary.failed7d ?? '—'}
            color={(data?.summary.failed7d ?? 0) > 0 ? 'red.7' : undefined}
          />
        </Group>
        <SegmentedControl
          value={onlyErrors ? 'errors' : 'all'}
          onChange={(v) => setOnlyErrors(v === 'errors')}
          data={[
            { label: 'Alle', value: 'all' },
            { label: 'Nur Fehler', value: 'errors' },
          ]}
          size="sm"
        />
      </Group>

      {isLoading && <Loader />}
      {data && data.notifications.length === 0 && (
        <Text c="dimmed" size="sm">
          Noch keine {onlyErrors ? 'fehlgeschlagenen ' : ''}Benachrichtigungen protokolliert.
        </Text>
      )}
      <Stack gap={6}>
        {data?.notifications.map((n) => (
          <NotificationRow key={n.id} n={n} />
        ))}
      </Stack>
    </Stack>
  );
}

function NotificationRow({ n }: { n: AdminNotification }): JSX.Element {
  const typeBadge =
    n.pushType === 'test' ? 'Test' : n.pushType === 'error' ? 'Fehler-Push' : null;
  return (
    <Card withBorder padding="xs" radius="md">
      <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="wrap">
            <Badge size="sm" color={n.ok ? 'teal' : 'red'} variant="light">
              {n.ok ? '✓' : '✗'} {CHANNEL_LABEL[n.channel] ?? n.channel}
            </Badge>
            {typeBadge && (
              <Badge size="sm" color="gray" variant="light">
                {typeBadge}
              </Badge>
            )}
            {n.score != null && (
              <Text size="xs" c="dimmed">
                {Math.round(n.score * 100)}%
              </Text>
            )}
          </Group>
          <Text size="sm" style={{ wordBreak: 'break-word' }}>
            {n.title || '(ohne Titel)'}
          </Text>
          <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
            {n.userName ?? n.userEmail ?? `User ${n.userId}`}
            {n.objectId ? ` · Objekt #${n.objectId}` : ''}
          </Text>
          {!n.ok && n.error && (
            <Text size="xs" c="red.7" style={{ wordBreak: 'break-word' }}>
              {n.error}
            </Text>
          )}
        </Stack>
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {relTime(n.createdAt)}
        </Text>
      </Group>
    </Card>
  );
}

function FailuresSection(): JSX.Element {
  const qc = useQueryClient();
  const [stage, setStage] = useState<string>('');
  const { data, isLoading } = useQuery({
    queryKey: ['admin-failures', stage],
    queryFn: () => getAdminFailures({ stage: stage || undefined, limit: 200 }),
    refetchInterval: 30_000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: number) => resolveFailure(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-failures'] });
      notifications.show({ message: 'Als erledigt markiert', color: 'teal' });
    },
    onError: (e) => notifications.show({ message: `Fehler: ${String(e)}`, color: 'red' }),
  });

  const stages = data ? Object.entries(data.openByStage) : [];
  const mailboxCount = data?.mailboxErrors.length ?? 0;
  // Postfach-Fehler zählen zu den offenen Problemen, auch wenn sie aus users.mailLastError
  // (nicht failure_log) kommen — sonst widerspricht „Alle offen (0)" der roten Postfach-Karte.
  const totalOpen = stages.reduce((s, [, c]) => s + c, 0) + mailboxCount;

  return (
    <Stack gap="md">
      {/* Kachel-Übersicht offener Fehler pro Stufe */}
      <Group gap="xs" wrap="wrap">
        <StageChip
          label={`Alle offen (${totalOpen})`}
          active={stage === ''}
          onClick={() => setStage('')}
        />
        {stages.map(([s, c]) => (
          <StageChip
            key={s}
            label={`${FAILURE_STAGE_LABELS[s] ?? s} (${c})`}
            active={stage === s}
            onClick={() => setStage(s)}
          />
        ))}
      </Group>

      {/* Postfach-Fehler (aus users.mailLastError) */}
      {data && data.mailboxErrors.length > 0 && (
        <Card withBorder padding="sm" radius="md" style={{ borderColor: '#fca5a5' }}>
          <Stack gap={4}>
            <Text fw={700} size="sm" c="red.8">
              📮 Aktive Postfach-Fehler
            </Text>
            {data.mailboxErrors.map((m) => (
              <Text key={m.userId} size="xs" style={{ wordBreak: 'break-word' }}>
                <b>{m.userName ?? m.userEmail}</b>: {m.error} <Text component="span" c="dimmed">({relTime(m.at)})</Text>
              </Text>
            ))}
          </Stack>
        </Card>
      )}

      {isLoading && <Loader />}
      {data && data.failures.length === 0 && mailboxCount === 0 && (
        <Text c="dimmed" size="sm">
          Keine offenen Fehler{stage ? ` in „${FAILURE_STAGE_LABELS[stage] ?? stage}"` : ''}. 🎉
        </Text>
      )}
      <Stack gap={6}>
        {data?.failures.map((f) => (
          <FailureRow key={f.id} f={f} onResolve={() => resolveMut.mutate(f.id)} busy={resolveMut.isPending} />
        ))}
      </Stack>
    </Stack>
  );
}

function FailureRow({
  f,
  onResolve,
  busy,
}: {
  f: AdminFailure;
  onResolve: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <Card withBorder padding="xs" radius="md">
      <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="wrap">
            <Badge size="sm" color="orange" variant="light">
              {FAILURE_STAGE_LABELS[f.stage] ?? f.stage}
            </Badge>
            {f.provider && (
              <Badge size="sm" color="gray" variant="light">
                {f.provider}
              </Badge>
            )}
            {f.objectId && (
              <Text
                size="xs"
                c="blue.7"
                component={Link}
                to={`/objects/${f.objectId}`}
                style={{ textDecoration: 'none' }}
              >
                Objekt #{f.objectId}
              </Text>
            )}
          </Group>
          <Text size="sm" style={{ wordBreak: 'break-word' }}>
            {f.message}
          </Text>
          <Text size="xs" c="dimmed">
            {(f.userName ?? f.userEmail) ? `${f.userName ?? f.userEmail} · ` : ''}
            {relTime(f.createdAt)}
          </Text>
        </Stack>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={onResolve} disabled={busy}>
          Erledigt
        </Button>
      </Group>
    </Card>
  );
}

function StageChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      size="compact-sm"
      variant={active ? 'filled' : 'light'}
      color={active ? 'blue' : 'gray'}
      radius="xl"
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

function Metric({ label, value, color }: { label: string; value: number | string; color?: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em', fontSize: '0.62rem' }}>
        {label}
      </Text>
      <Text fw={800} size="lg" c={color} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}
